<template>
  <div class="send-push-notification-form">
    <Loader v-if="isLoading" />

    <!-- Template Selector -->
    <TemplateSelectorV2
      v-else-if="templateSelectorIsOpen"
      type-templates="pushnotifications"
      :origin-call="campaignType"
      :id-campaign="campaignId"
      :duplicate="true"
      :current-template-name="templateConfig.activePreviewTemplate.value.label"
      @on-choose-template="handleSelectTemplate"
      @on-close-dialog="handleCloseTemplateSelector"
    />

    <!-- Main Configuration -->
    <PanelAccordion
      v-else
      :key="panelsKey"
      :always-keep-one-open="false"
      :panels="panels"
    >
      <!-- Template Preview -->
      <template #template_preview>
        <TemplatePreview
          :templates="templateConfig.listTemplates.value"
          :active-media="templateConfig.activePreviewMedia.value"
          :active-template-index="templateConfig.activePreviewTemplateIndex.value"
          :image-load-time="templateConfig.imageLoadTime.value"
          :image-refresh-key="templateConfig.tmpImgRefreshKey.value"
          :show-send-test="false"
          @tab-change="templateConfig.handleActivePreviewTemplateChange"
          @select-media="templateConfig.selectPreviewMedia"
          @change-template="handleChangeTemplate"
          @edit-template="handleEditTemplate"
          @image-loaded="templateConfig.handleImageLoaded"
          @image-failed="templateConfig.handleImageFailed"
        />
      </template>

      <!-- Voucher Configuration (Multi-voucher or legacy) -->
      <template #voucher_config>
        <!-- Multi-voucher system: reuse selector is inside each template's modal -->
        <MultiVoucherConfig
          v-if="useMultiVoucherConfig && dataFields.template_vouchers"
          v-model="dataFields.template_vouchers"
          :templates="templateConfig.listTemplates.value"
          :form-validation="formValidationErrors"
          :allow-automatic-method="templateConfig.allowAutomaticVoucherMethod.value"
          :show-reuse-previous-coupon-field="showReusePreviousCouponField"
          :reuse-previous-coupon-options="reusePreviousCouponOptions"
          :resolved-parent-voucher="resolvedParentVoucher"
          :resolved-expiring-voucher="resolvedExpiringVoucher"
        />
        <!-- Legacy VoucherConfig with global reuse selector -->
        <template v-else>
          <ReuseCouponSelector
            v-if="showReusePreviousCouponField"
            v-model="dataFields.reuse_a_previous_coupon"
            :options="reusePreviousCouponOptions"
          />
          <VoucherConfig
            v-if="showVoucherConfig"
            v-model="dataFields"
            :form-validation="formValidationErrors"
            :allow-automatic-method="templateConfig.allowAutomaticVoucherMethod.value"
          />
        </template>
      </template>

      <!-- UTM Configuration -->
      <template #utm_config>
        <UtmConfig v-model="utmData" />
      </template>
    </PanelAccordion>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  reactive,
  computed,
  watch,
  toRef,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Loader from '@/components/layout/Loader.vue';
import { PanelAccordion } from '@/components/flows/operators/shared';
import TemplateSelectorV2 from '@/components/automated-scenarios/fields/TemplateSelectorV2.vue';
import { TemplatePreview, VoucherConfig, MultiVoucherConfig, ReuseCouponSelector, UtmConfig } from '@/components/flows/operators/shared';
import { useSendingChannelForm } from '@/composables/flows';
import { useUtmData } from '@/components/flows/operators/shared/utmDefaults';
import { showToastError } from '@/helpers';
import type { SendPushNotificationData } from './index';
import { createDefaultData } from './index';

export default defineComponent({
  name: 'ActionSendPushNotificationForm',

  components: {
    Loader,
    PanelAccordion,
    TemplateSelectorV2,
    TemplatePreview,
    VoucherConfig,
    MultiVoucherConfig,
    ReuseCouponSelector,
    UtmConfig,
  },

  props: {
    modelValue: {
      type: Object as PropType<SendPushNotificationData>,
      required: true,
      default: () => createDefaultData(),
    },

    formValidation: {
      type: Object,
      default: () => ({}),
    },

    selectedComponent: {
      type: String,
      default: '',
    },

    operatorId: {
      type: String,
      default: undefined,
    },
  },

  emits: ['update:modelValue', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const panelsKey = ref(0);

    // Reactive data fields
    const dataFields = reactive<SendPushNotificationData>(createDefaultData({ ...props.modelValue }));

    // Initialize sending channel form composable
    const sendingChannelForm = useSendingChannelForm({
      media: 'pushnotifications',
      dataFields,
      formValidation: toRef(props, 'formValidation'),
      panelsKey,
      operatorId: props.operatorId,
      onCancel: () => emit('cancel'),
    });

    // Destructure commonly used values from composable
    const {
      campaignId,
      campaignType,
      templateConfig,
      templateSelectorIsOpen,
      formValidationErrors,
      voucherData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      initialize,
    } = sendingChannelForm;

    // UTM data computed
    const utmData = useUtmData(dataFields);

    // Panels configuration (no message_config for push notifications)
    const panels = computed(() => [
      {
        id: 'template_preview',
        title: 'automatedScenarios.actions.sending_channel.template_preview.title',
      },
      {
        id: 'voucher_config',
        title: 'automatedScenarios.actions.sending_channel.vouchers_config.title',
        hide: !showVoucherPanel.value,
      },
      {
        id: 'utm_config',
        title: 'automatedScenarios.actions.sending_channel.utm_config.title',
      },
    ]);

    // Emit updates when data changes
    watch(dataFields, () => {
      emit('update:modelValue', createDefaultData({ ...dataFields }));
    }, { deep: true });

    // Initialize on mount
    onMounted(async () => {
      try {
        isLoading.value = true;
        await initialize(dataFields.template_display_selector_id);
      } catch (error) {
        showToastError('GENERIC_ERROR');
      } finally {
        isLoading.value = false;
      }
    });

    return {
      t,
      isLoading,
      panelsKey,
      formValidationErrors,
      templateConfig,
      templateSelectorIsOpen,
      dataFields,
      voucherData,
      showVoucherPanel,
      showVoucherConfig,
      showReusePreviousCouponField,
      reusePreviousCouponOptions,
      useMultiVoucherConfig,
      resolvedParentVoucher,
      resolvedExpiringVoucher,
      panels,
      campaignId,
      campaignType,
      handleSelectTemplate,
      handleCloseTemplateSelector,
      handleChangeTemplate,
      handleEditTemplate,
      utmData,
    };
  },
});
</script>
